import { useState } from "react";
import { ChevronDown, Clock, RotateCcw, Undo2, XCircle } from "lucide-react";
import {
  Badge, Button, Card, CardContent, CardHeader, CardTitle, UsageBar, cn, localeText,
} from "@wa/ui";
import { fmtMoney, genericStatusVariant } from "../../../lib/format";
import { UNLIMITED, type BillingStatus, type EntitlementDef } from "../../../lib/billing";
import { EntitlementList } from "./EntitlementList";
import { countEntitlements, isGranted, planPriceCents, type AccountPlanGroup } from "./lib";

type T = (k: string, v?: Record<string, string | number>) => string;

/** Site chips shown before the "+N more" toggle appears. Four fits one row at 375px once
 *  each chip is capped, which is what keeps the collapsed card one screen tall on a phone. */
const SITE_CHIP_LIMIT = 4;

/**
 * One billing account's current plan.
 *
 * Replaces the old per-site card. Everything here except the site names is account-level,
 * so N sibling sites produce ONE card instead of N identical ones. The entitlement list is
 * the tail of the card, not its body: 41 of the 52 catalog entries render as nothing but a
 * tick or a cross, so showing them all by default buried the two facts a customer opens
 * this tab for - what am I paying, and when does it renew.
 */
export function AccountPlanCard({
  group, catalog, status, locale, t, resumeBusy, onCancelClick, onResumeClick, onRefundClick, onViewUsage,
}: {
  group: AccountPlanGroup;
  catalog: EntitlementDef[];
  /** The account subscription's Stripe status. Only meaningful for the owning group. */
  status: BillingStatus | null;
  locale: string;
  t: T;
  resumeBusy: boolean;
  onCancelClick: () => void;
  onResumeClick: () => void;
  onRefundClick: () => void;
  onViewUsage: () => void;
}) {
  const [showIncluded, setShowIncluded] = useState(false);
  const [showMissing, setShowMissing] = useState(false);
  const [showAllSites, setShowAllSites] = useState(false);

  const loc = locale === "et" ? "et" : "en";
  const site = group.primary;
  const planName = site.plan ? localeText(site.plan.name, loc) : site.planCode;
  const cycle = site.billingCycle;
  const priceCents = planPriceCents(site.plan, cycle);
  const currency = site.plan?.currency ?? "EUR";
  const singleSite = group.sites.length === 1;

  const granted = catalog.filter((d) => isGranted(d, site.features));
  const missing = catalog.filter((d) => !isGranted(d, site.features));
  const counts = countEntitlements(catalog, site.features);

  // Cancel/resume/refund and the renewal date all describe ONE Stripe subscription, resolved
  // from a single X-Site-Id request. Rendering them on a second account's card would state
  // the wrong date and cancel the wrong subscription.
  const manage = group.ownsSubscriptionControls ? status : null;
  const dateFmt = (iso: string) => new Date(iso).toLocaleDateString(loc === "et" ? "et-EE" : "en-US");
  const periodEnd = manage?.currentPeriodEnd ? dateFmt(manage.currentPeriodEnd) : null;
  const trialEndsAt = manage?.status === "trialing" ? manage.trialEndsAt : null;
  const trialDaysLeft = trialEndsAt
    ? Math.max(0, Math.ceil((new Date(trialEndsAt).getTime() - Date.now()) / 86_400_000))
    : 0;
  const isFreePlan = (manage?.planCode ?? "free") === "free";
  const canCancel =
    manage != null && !isFreePlan && manage.hasStripeCustomer &&
    (manage.status === "active" || manage.status === "trialing") && !manage.cancelAtPeriodEnd;
  const canResume = manage?.cancelAtPeriodEnd === true;
  const periodLine = trialEndsAt
    ? t("billing.trialCountdown", { date: dateFmt(trialEndsAt), days: trialDaysLeft })
    : manage?.cancelAtPeriodEnd && periodEnd
      ? t("billing.cancelsOn", { date: periodEnd })
      : periodEnd
        ? t("billing.renewsOn", { date: periodEnd })
        : "";

  const visibleSites = showAllSites ? group.sites : group.sites.slice(0, SITE_CHIP_LIMIT);
  const hiddenSites = group.sites.length - visibleSites.length;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t("billing.currentPlan")}
              {/* With one site the name rides along here; with several it moves to the chip
                  rail below, where it does not compete with the plan name. */}
              {singleSite && (
                <span className="ml-1 normal-case text-muted-foreground/70">· {site.siteName}</span>
              )}
            </p>
            <div className="mt-1 flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
              <CardTitle className="text-2xl font-bold tracking-tight">{planName}</CardTitle>
              <span className="text-sm tabular-nums text-muted-foreground">
                {priceCents > 0
                  ? `${fmtMoney(priceCents, currency, locale)}/${cycle === "yearly" ? t("billing.perYear") : t("billing.perMonth")}`
                  : t("billing.free")}
              </span>
              {priceCents > 0 && (
                <Badge variant="outline">{t(`billing.cycle.${cycle}`)}</Badge>
              )}
            </div>
          </div>
          {/* A green "Active" chip on every card is zero signal. The badge appears only when
              the state is something the user should act on; "active" is confirmed by the
              renewal line instead. */}
          {site.status !== "active" && (
            <Badge variant={genericStatusVariant(site.status)}>{t(`billing.status.${site.status}`)}</Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-3.5">
        {(periodLine || canCancel || canResume) && (
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border/60 pt-3">
            {periodLine && (
              <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <Clock className="size-3.5 shrink-0" /> {periodLine}
              </p>
            )}
            {canResume ? (
              <Button variant="outline" size="sm" className="ml-auto" onClick={onResumeClick} loading={resumeBusy}>
                <RotateCcw className="size-3.5" /> {t("billing.resume")}
              </Button>
            ) : canCancel ? (
              <Button variant="ghost" size="sm" className="ml-auto text-muted-foreground" onClick={onCancelClick}>
                <XCircle className="size-3.5" /> {t("billing.cancelSub")}
              </Button>
            ) : null}
          </div>
        )}

        {site.quota && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <UsageBar
              label={t("billing.siteQuota")}
              used={site.quota.used}
              limit={site.quota.limit}
              unlimited={site.quota.limit === UNLIMITED}
              // Explicit thresholds. UsageBar defaults `exceeded` to used >= limit, which
              // paints a Free account sitting at exactly 1 / 1 in destructive red - using
              // every site your plan grants is the normal, correct state, not an error.
              // Amber at full, red only once genuinely over.
              warn={site.quota.limit !== UNLIMITED && site.quota.used >= site.quota.limit}
              exceeded={site.quota.limit !== UNLIMITED && site.quota.used > site.quota.limit}
              compact
            />
            <button
              type="button"
              onClick={onViewUsage}
              className="text-xs font-semibold text-primary transition-colors hover:underline"
            >
              {t("billing.viewUsage")}
            </button>
          </div>
        )}

        {/* The chip rail lists the sites THIS USER manages under the plan, which is not
            necessarily every site the quota counts (the quota counts the whole account, a
            manager may only hold some of it). The label says "your sites" for that reason. */}
        {!singleSite && (
          <div>
            <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70">
              {t("billing.yourSitesOnPlan")}
            </p>
            <div className="flex flex-wrap items-center gap-1.5">
              {visibleSites.map((s) => (
                <Badge key={s.siteId} variant="secondary" className="max-w-[10rem] truncate" title={s.siteName}>
                  {s.siteName}
                </Badge>
              ))}
              {(hiddenSites > 0 || showAllSites) && (
                <button
                  type="button"
                  onClick={() => setShowAllSites((v) => !v)}
                  className="text-xs font-semibold text-primary transition-colors hover:underline"
                >
                  {showAllSites ? t("billing.sitesLess") : t("billing.sitesMore", { n: hiddenSites })}
                </button>
              )}
            </div>
          </div>
        )}

        <div className="space-y-2 border-t border-border/60 pt-3">
          <Disclosure
            open={showIncluded}
            onToggle={() => setShowIncluded((v) => !v)}
            label={`${t("billing.included")} (${counts.granted})`}
          />
          {showIncluded && <EntitlementList defs={granted} features={site.features} locale={locale} t={t} />}

          {counts.missing > 0 && (
            <>
              <Disclosure
                open={showMissing}
                onToggle={() => setShowMissing((v) => !v)}
                label={`${t("billing.notIncluded")} (${counts.missing})`}
                muted
              />
              {showMissing && (
                <EntitlementList defs={missing} features={site.features} locale={locale} t={t} muted />
              )}
            </>
          )}
        </div>

        {manage?.refundEligible && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-muted/30 p-2.5">
            <p className="text-xs text-muted-foreground">
              <Clock className="mr-1 inline size-3.5 -translate-y-px" />
              {t("billing.refundGuarantee", { date: manage.refundDeadline ? dateFmt(manage.refundDeadline) : "-" })}
            </p>
            <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={onRefundClick}>
              <Undo2 className="size-3.5" /> {t("billing.requestRefund")}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** Show/hide row for one entitlement panel. Same plain conditional-render toggle the plan
 *  comparison uses (no Radix collapsible exists in this design system). */
function Disclosure({
  open, onToggle, label, muted,
}: {
  open: boolean;
  onToggle: () => void;
  label: string;
  muted?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className={cn(
        "flex w-full items-center gap-1.5 rounded-md py-1 text-sm font-medium transition-colors hover:text-foreground",
        muted ? "text-muted-foreground/70" : "text-muted-foreground",
      )}
    >
      {label}
      <ChevronDown className={cn("size-4 transition-transform", open && "rotate-180")} />
    </button>
  );
}
